// MARK://dhk8/pos/pages/PettyCashListPage
// Lista de retiros petty-cash ("Compra a proveedor") — ruta /cashout/retiros.
// FASE 1 del ciclo 2-fases (ver CashoutPage) abre estos retiros como OPEN sin
// itemizar; esta lista es el punto de entrada para CONCILIARLOS (o cancelarlos)
// en PettyCashDetailPage — potencialmente en otro turno / otro cajero.
//
// Reusa el mismo lenguaje de tabla que Tickets (`FilterableTable` +
// `StatusChip`) — ver apps/pos/src/pages/TicketsPage.tsx para el patrón.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Wallet } from 'lucide-react'

import { listPettyCashPurchases, type PettyCashPurchaseSummary } from '../lib/clients/petty-cash.js'
import { getUserById } from '../lib/clients/users.js'
import { formatCents } from '../lib/money.js'
import { FilterableTable, StatusChip, type ChipTone, type FilterableColumn } from '../components/FilterableTable.js'

function fmtDateMx(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }).format(d)
}
function fmtTimeMx(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat('es-MX', { hour: '2-digit', minute: '2-digit' }).format(d)
}

const STATUS_LABEL: Record<string, string> = {
  OPEN: '⏳ Pendiente de conciliar',
  RECONCILED: '✓ Conciliado',
  CANCELLED: 'Cancelado',
}
const STATUS_TONE: Record<string, ChipTone> = {
  OPEN: 'warning',
  RECONCILED: 'success',
  CANCELLED: 'neutral',
}
const STATUS_OPTIONS = [
  { value: 'ALL', label: 'Todos' },
  { value: 'OPEN', label: 'Pendientes' },
  { value: 'RECONCILED', label: 'Conciliados' },
  { value: 'CANCELLED', label: 'Cancelados' },
]

/** Resuelve cashierId → nombre para las filas visibles. Best-effort: ante error
 *  de red/permiso el nombre cae al id truncado (no bloquea el listado). */
function useCashierNames(rows: PettyCashPurchaseSummary[]): Record<string, string> {
  const [names, setNames] = useState<Record<string, string>>({})
  useEffect(() => {
    const ids = Array.from(new Set(rows.map((r) => r.cashierId).filter((id): id is string => Boolean(id))))
    const missing = ids.filter((id) => !(id in names))
    if (missing.length === 0) return
    let alive = true
    const ctrl = new AbortController()
    Promise.all(missing.map((id) => getUserById(id, ctrl.signal).then((u) => [id, u?.fullName ?? null] as const)))
      .then((pairs) => {
        if (!alive) return
        setNames((prev) => {
          const next = { ...prev }
          for (const [id, name] of pairs) if (name) next[id] = name
          return next
        })
      })
      .catch(() => { /* best-effort — deja el fallback de id truncado */ })
    return () => { alive = false; ctrl.abort() }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `names` se lee sólo para el diff `missing`, no debe re-disparar el efecto.
  }, [rows])
  return names
}

export default function PettyCashListPage() {
  const navigate = useNavigate()

  const query = useQuery({
    queryKey: ['petty-cash', 'list'],
    queryFn: ({ signal }) => listPettyCashPurchases({ limit: 200 }).then((items) => {
      if (signal?.aborted) throw new Error('aborted')
      return items
    }),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  })
  const rows = query.data ?? []
  const cashierNames = useCashierNames(rows)

  const columns: FilterableColumn<PettyCashPurchaseSummary>[] = [
    {
      key: 'createdAt',
      header: 'Fecha y hora',
      sortable: true,
      sortAccessor: (r) => +new Date(r.createdAt),
      render: (r) => (
        <>
          <div className="nui-tk-when__date">{fmtDateMx(r.createdAt)}</div>
          <div className="nui-tk-when__time">{fmtTimeMx(r.createdAt)}</div>
        </>
      ),
    },
    {
      key: 'withdrawnCents',
      header: 'Retirado',
      sortable: true,
      sortAccessor: (r) => r.withdrawnCents,
      align: 'right',
      className: 'nui-tk-num nui-tk-total',
      render: (r) => formatCents(r.withdrawnCents),
    },
    {
      key: 'cashierId',
      header: 'Cajero',
      render: (r) => (r.cashierId ? (cashierNames[r.cashierId] ?? `${r.cashierId.slice(0, 8)}…`) : '—'),
    },
    {
      key: 'status',
      header: 'Estatus',
      render: (r) => <StatusChip tone={STATUS_TONE[r.status] ?? 'neutral'}>{STATUS_LABEL[r.status] ?? r.status}</StatusChip>,
    },
  ]

  return (
    <div className="dhk8-tk-root">
      <header className="dhk8-tk-head">
        <div>
          <div className="dhk8-tk-eyebrow">Caja · Petty-cash</div>
          <h1 className="dhk8-tk-title">Retiros pendientes</h1>
          <div className="dhk8-tk-sub">Compras a proveedor abiertas con efectivo del cajón — concílialas o cancélalas.</div>
        </div>
        <Wallet size={20} className="dhk8-tk-head-icon" aria-hidden />
      </header>

      <FilterableTable<PettyCashPurchaseSummary>
        rows={rows}
        columns={columns}
        getRowKey={(r) => r.id}
        loading={query.isLoading}
        ariaLabel="Listado de retiros petty-cash"
        statusOptions={STATUS_OPTIONS}
        statusAccessor={(r) => r.status}
        initialSort={{ key: 'createdAt', dir: 'desc' }}
        pageSize={25}
        emptyState={rows.length === 0 ? 'No hay retiros de petty-cash registrados.' : 'Ningún retiro coincide con los filtros.'}
        renderSummary={(filtered) => {
          let totalWithdrawn = 0, openCount = 0, reconciledCount = 0
          for (const r of filtered) {
            totalWithdrawn += r.withdrawnCents
            if (r.status === 'OPEN') openCount++
            else if (r.status === 'RECONCILED') reconciledCount++
          }
          return (
            <>
              <span><strong>{filtered.length.toLocaleString('es-MX')}</strong> retiros</span>
              <span className="nui-tk-summary__sep">·</span>
              <span><strong>{formatCents(totalWithdrawn)}</strong> retirado en total</span>
              {openCount > 0 && (<><span className="nui-tk-summary__sep">·</span><span>{openCount} pendiente{openCount === 1 ? '' : 's'}</span></>)}
              {reconciledCount > 0 && (<><span className="nui-tk-summary__sep">·</span><span>{reconciledCount} conciliado{reconciledCount === 1 ? '' : 's'}</span></>)}
            </>
          )
        }}
        onRowClick={(r) => navigate(`/cashout/retiros/${encodeURIComponent(r.id)}`)}
        rowAriaLabel={(r) => `Retiro ${formatCents(r.withdrawnCents)} · ${STATUS_LABEL[r.status] ?? r.status}`}
        rowActions={(r) => (
          <button
            type="button"
            className="nui-tk-act nui-tk-act--pay"
            onClick={() => navigate(`/cashout/retiros/${encodeURIComponent(r.id)}`)}
          >
            {r.status === 'OPEN' ? 'Conciliar' : 'Ver'}
          </button>
        )}
      />

      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .dhk8-tk-root { display:flex; flex-direction:column; gap:var(--pos-space-4,16px); min-height:0; flex:1; overflow-y:auto; -webkit-overflow-scrolling:touch; padding-bottom:var(--pos-space-4,16px); }
  .dhk8-tk-head { display:flex; justify-content:space-between; align-items:flex-start; gap:var(--pos-space-3,12px); }
  .dhk8-tk-head-icon { color:var(--pos-fg-tertiary,#7E8C82); flex-shrink:0; margin-top:2px; }
  .dhk8-tk-eyebrow { font-family:var(--font-mono,ui-monospace,monospace); font-size:var(--pos-text-xxs,11px); letter-spacing:.12em; text-transform:uppercase; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-tk-title { font-family:var(--font-display,system-ui); font-size:var(--pos-text-2xl,24px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg,#E8EFE9); margin:var(--pos-space-1,4px) 0 0; }
  .dhk8-tk-sub { font-family:var(--font-mono,ui-monospace,monospace); font-size:var(--pos-text-xs,12px); color:var(--pos-fg-secondary,#B6BFB8); margin-top:2px; }

  .nui-tk-when__date { color:var(--pos-fg,#E8EFE9); }
  .nui-tk-when__time { font-family:var(--font-mono,ui-monospace,monospace); font-size:var(--pos-text-xxs,11px); color:var(--pos-fg-tertiary,#7E8C82); font-variant-numeric:tabular-nums; margin-top:2px; }
  .nui-tk-num { font-family:var(--font-mono,ui-monospace,monospace); font-variant-numeric:tabular-nums; }
  .nui-tk-total { color:var(--pos-fg,#E8EFE9); font-weight:var(--pos-weight-medium,500); }

  .nui-tk-act { min-height:44px; padding:0 var(--pos-space-4,16px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-elevated,#1E2520); color:var(--pos-fg,#E8EFE9); font-family:var(--font-display,inherit); font-size:var(--pos-text-xs,12px); font-weight:var(--pos-weight-semibold,600); cursor:pointer; white-space:nowrap; }
  .nui-tk-act:hover { background:var(--pos-surface,#161C18); }
  .nui-tk-act--pay { background:var(--pos-tint,#1F8B3A); border-color:var(--pos-tint,#1F8B3A); color:#fff; }
  .nui-tk-act--pay:hover { background:var(--pos-tint-hover,#1a7a33); }
`
